// TabMeasureRow — the hidden, aria-hidden measurement row for the adaptive
// contract tab bar. It renders every tab plus the More button at full size so
// useAdaptiveTabFit can read their natural widths. It is a sibling of the
// tablist (never a child) so it cannot inflate the tab count exposed to
// assistive tech, is never active, carries tabIndex={-1}, and uses
// measure-prefixed panel ids so it never collides with the live tabs' ids or
// the real tab panels.
//
// It renders the SAME TabLabelWithAttention markup (including the attention dot)
// as the live rows so the measured width matches what is rendered — the dot adds
// real width, and omitting it here would make the measurement under-report. The
// sr-only "needs attention" suffix is zero-width, so it does not affect parity.

import { type RefObject } from "react";
import { TabButton } from "../../common";
import MoreSectionsButton from "./MoreSectionsButton";
import TabLabelWithAttention from "./TabLabelWithAttention";
import { CONTRACT_TABS, type TabSlug } from "./tabRegistry";

interface TabMeasureRowProps {
  badgeCounts: Partial<Record<TabSlug, number | undefined>>;
  attention: Partial<Record<TabSlug, boolean>>;
  itemRefs: RefObject<Array<HTMLElement | null>>;
  moreRef: RefObject<HTMLButtonElement | null>;
}

export default function TabMeasureRow({
  badgeCounts,
  attention,
  itemRefs,
  moreRef,
}: TabMeasureRowProps) {
  return (
    <div
      aria-hidden="true"
      className="absolute invisible h-0 overflow-hidden pointer-events-none flex items-center gap-8"
    >
      {CONTRACT_TABS.map(({ slug, label, icon: Icon }, index) => (
        <TabButton
          key={slug}
          ref={(el) => {
            itemRefs.current[index] = el;
          }}
          active={false}
          tabIndex={-1}
          panelId={`measure-${slug}-panel`}
          icon={<Icon className="h-4 w-4" />}
          badge={badgeCounts[slug]}
          data-measure={`measure-${slug}`}
        >
          <TabLabelWithAttention label={label} attention={attention[slug]} />
        </TabButton>
      ))}
      <MoreSectionsButton
        ref={(el) => {
          moreRef.current = el;
        }}
        hiddenCount={0}
        open={false}
        hasAttention={false}
        onClick={() => {}}
        data-measure="measure-more"
      />
    </div>
  );
}
